'use client';

import { motion } from "framer-motion";
import Link from "next/link";
import { ArrowLeft, CheckCircle, TrendingUp, BarChart, Target, Activity } from "lucide-react";
import { useParams } from "next/navigation";

const predictiveAnalyticsData = {
  title: "Predictive Analytics Platform",
  subtitle: "Advanced forecasting and data insights to drive strategic business decisions.",
  headerGradient: "bg-gradient-to-r from-purple-500 via-indigo-600 to-blue-600",
  accentColor: "text-purple-600",
  buttonColor: "bg-purple-600 hover:bg-purple-700",
  challenge: "Businesses need to anticipate market trends, customer behavior, and operational bottlenecks before they impact performance. Traditional reactive approaches lead to missed opportunities and increased costs.",
  solutionHighlights: [
    "Built advanced machine learning models for demand forecasting and trend prediction",
    "Implemented real-time data processing pipelines for continuous model training",
    "Created interactive dashboards for data exploration and insight visualization",
    "Integrated automated alert systems for anomaly detection and early warning"
  ],
  benefits: [
    "85% improvement in forecast accuracy",
    "60% reduction in inventory holding costs",
    "40% faster decision-making processes",
    "Proactive risk mitigation capabilities"
  ],
  techStack: ["Machine Learning", "Python", "TensorFlow", "Apache Spark", "Real-time Analytics", "Business Intelligence"],
  analyticsStats: {
    modelsDeployed: "47",
    predictionAccuracy: "94.2%",
    dataProcessed: "2.3TB",
    insights: "156"
  }
};

export default function PredictiveAnalyticsPage() {
  const params = useParams();
  const currentLocale = (params?.locale as string) || 'en';
  
  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      {/* Header Section */}
      <header className={`py-16 md:py-24 text-white ${predictiveAnalyticsData.headerGradient}`}>
        <div className="container mx-auto max-w-4xl px-4 text-center">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
            <Link href={`/${currentLocale}/portfolio`} className="inline-flex items-center text-sm text-white hover:text-purple-100 transition-colors duration-300 bg-white/10 hover:bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full shadow-md">
              <ArrowLeft size={18} className="mr-2" />
              Back to Portfolio
            </Link>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            {predictiveAnalyticsData.title}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-lg md:text-xl text-purple-100 max-w-3xl mx-auto">
            {predictiveAnalyticsData.subtitle}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mt-8 flex flex-wrap gap-3 justify-center">
            {predictiveAnalyticsData.techStack.slice(0, 3).map(tech => (
              <span key={tech} className="bg-white/20 text-white px-3 py-1 rounded-full text-xs font-medium">
                {tech}
              </span>
            ))}
          </motion.div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="container mx-auto max-w-5xl px-4 py-12 md:py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white p-8 md:p-12 rounded-xl shadow-xl -mt-20 md:-mt-32 relative z-10">
          
          {/* Analytics Dashboard Visual Section */}
          <section className="mb-12">
            <h2 className={`text-3xl font-semibold mb-8 text-center ${predictiveAnalyticsData.accentColor}`}>Analytics Dashboard</h2>
            <div className="bg-slate-100 p-4 sm:p-6 rounded-lg shadow-lg border border-slate-200">
              <div className="flex items-center justify-between px-4 py-2 mb-4 bg-gradient-to-r from-slate-700 to-slate-800 text-white text-sm font-semibold rounded-t-md shadow">
                <span>Predictive Insights Platform</span>
                <span className="text-xs bg-purple-500/90 px-2 py-0.5 rounded-full flex items-center">
                  <TrendingUp size={12} className="mr-1"/>
                  Live Analysis
                </span>
              </div>
              <div className="flex flex-col md:flex-row gap-4 min-h-[280px]">
                {/* Analytics Chart Simulation */}
                <div className="flex-1 flex items-center justify-center bg-slate-200 rounded-md p-4 relative min-h-[200px]">
                  <div className="w-full h-full bg-slate-800 rounded-lg relative overflow-hidden shadow-inner border border-slate-700 flex flex-col p-4">
                    <div className="absolute top-2 left-2 right-2 flex justify-between items-center">
                      <div className="flex items-center">
                        <BarChart size={18} className="text-purple-400 mr-1" />
                        <span className="text-slate-200 font-medium">Trend Analysis</span>
                      </div>
                      <div className="bg-purple-900/50 text-purple-200 px-2 py-0.5 rounded text-xs font-medium">
                        Real-time
                      </div>
                    </div>
                    
                    <div className="mt-10 flex-1 relative">
                      {/* Simulated chart with trend lines */}
                      <div className="w-full h-full bg-slate-700 rounded relative">
                        {/* Grid lines */}
                        <div className="absolute inset-0 grid grid-cols-8 grid-rows-6 opacity-20">
                          {[...Array(48)].map((_, i) => (
                            <div key={i} className="border border-slate-500/30"></div>
                          ))}
                        </div>
                        
                        {/* Trend lines */}
                        <svg className="absolute inset-0 w-full h-full">
                          <path d="M20 120 Q60 100 100 80 T180 60 T260 40" stroke="#8b5cf6" strokeWidth="3" fill="none" opacity="0.8"/>
                          <path d="M20 140 Q80 130 140 120 T220 100 T280 80" stroke="#06b6d4" strokeWidth="2" fill="none" opacity="0.6"/>
                        </svg>
                        
                        {/* Data points */}
                        <div className="absolute top-10 left-12 w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
                        <div className="absolute top-16 left-24 w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></div>
                        <div className="absolute top-8 right-16 w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
                        
                        {/* Legend */}
                        <div className="absolute bottom-2 left-2 text-xs text-slate-300 space-y-1">
                          <div className="flex items-center">
                            <div className="w-3 h-0.5 bg-purple-400 mr-2"></div>
                            <span>Predictions</span>
                          </div>
                          <div className="flex items-center">
                            <div className="w-3 h-0.5 bg-cyan-400 mr-2"></div>
                            <span>Actuals</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Sidebar Analytics Stats */}
                <div className="w-full md:w-52 bg-slate-50 rounded-md p-4 space-y-2.5 ring-1 ring-slate-200 flex flex-col justify-center">
                  {[ 
                    { label: "Models Deployed", value: predictiveAnalyticsData.analyticsStats.modelsDeployed, color: "text-purple-700", bgColor:"bg-purple-100", icon: Target }, 
                    { label: "Prediction Accuracy", value: predictiveAnalyticsData.analyticsStats.predictionAccuracy, color: "text-green-700", bgColor:"bg-green-100", icon: TrendingUp }, 
                    { label: "Data Processed", value: predictiveAnalyticsData.analyticsStats.dataProcessed, color: "text-blue-700", bgColor:"bg-blue-100", icon: BarChart }, 
                    { label: "Insights Generated", value: predictiveAnalyticsData.analyticsStats.insights, color: "text-orange-700", bgColor:"bg-orange-100", icon: Activity } 
                  ].map(stat => (
                    <div key={stat.label} className={`flex items-center justify-between p-2 rounded ${stat.bgColor}`}>
                      <div className="flex items-center">
                        <stat.icon size={14} className={`${stat.color} mr-1`} />
                        <span className={`text-xs font-medium ${stat.color}`}>{stat.label}</span>
                      </div>
                      <span className={`font-bold text-sm ${stat.color}`}>{stat.value}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="px-4 py-1.5 mt-3 bg-slate-200 border-t border-slate-300 text-xs text-slate-600 flex items-center gap-2 rounded-b-md">
                <span className="w-2 h-2 rounded-full bg-purple-500 inline-block animate-pulse"></span>
                <span>Analytics engine active. Last model update: 3 minutes ago.</span>
              </div>
            </div>
          </section>

          <div className="grid md:grid-cols-1 gap-10">
            <section>
              <h2 className={`text-2xl font-semibold mb-4 ${predictiveAnalyticsData.accentColor}`}>The Challenge</h2>
              <p className="text-slate-700 leading-relaxed whitespace-pre-line">
                {predictiveAnalyticsData.challenge}
              </p>
            </section>

            <section>
              <h2 className={`text-2xl font-semibold mb-4 ${predictiveAnalyticsData.accentColor}`}>Our Solution</h2>
              <ul className="space-y-3 text-slate-700">
                {predictiveAnalyticsData.solutionHighlights.map((item, index) => (
                  <motion.li key={index} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.1 }} className="flex items-start pl-2">
                    <CheckCircle className={`w-5 h-5 ${predictiveAnalyticsData.accentColor} mr-3 mt-0.5 flex-shrink-0`} />
                    <span>{item}</span>
                  </motion.li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className={`text-2xl font-semibold mb-4 ${predictiveAnalyticsData.accentColor}`}>Key Benefits</h2>
              <div className="bg-purple-50 border border-purple-200 p-6 rounded-lg shadow-sm">
                <ul className="space-y-3">
                  {predictiveAnalyticsData.benefits.map((benefit, index) => (
                    <motion.li key={index} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.1 }} className="flex items-start text-purple-800">
                      <CheckCircle className="w-5 h-5 text-purple-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>{benefit}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
            </section>

            <section>
              <h2 className={`text-2xl font-semibold mb-5 ${predictiveAnalyticsData.accentColor}`}>Technologies Used</h2>
              <div className="flex flex-wrap gap-3">
                {predictiveAnalyticsData.techStack.map((tech, index) => (
                  <motion.span key={index} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: index * 0.05 }}
                    className="bg-slate-200 text-slate-700 px-4 py-1.5 rounded-full text-sm font-medium border border-slate-300 hover:bg-slate-300 transition-colors cursor-default">
                    {tech}
                  </motion.span>
                ))}
              </div>
            </section>
          </div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="mt-16 text-center">
            <Link href={`/${currentLocale}/contact`} className={`${predictiveAnalyticsData.buttonColor} text-white font-semibold py-3.5 px-10 rounded-lg text-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-50`}>
              Unlock Predictive Insights
            </Link>
          </motion.div>

        </motion.div>
      </main>
    </div>
  );
} 